import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';

export const InvestmentKnowledgeVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  workflowCompletion,
}: {
  options: any;
  userData: any;
  loading: boolean;
  updateUser: any;
  continueFunc: any;
  workflowCompletion?: any;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('client');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      investmentKnowledge: { required: true },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        investmentKnowledge: userData.investmentKnowledge,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, userData],
  );

  const submit = async () => {
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    if (!formErrors) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsInvestmentKnowledgeContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      await continueFunc();
    }
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='investment-knowledge-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: `investment-knowledge-${kebabCase(translateBackendString(options.title))}-continue-button`,
      }}
    >
      <Box
        sx={{
          mb: 3,
        }}
      >
        {options?.title && (
          <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
            {translateBackendString(options?.title)}
            <Box
              sx={{
                display: 'inline-flex',
              }}
            >
              {additionalInfo(
                translateBackendString,
                options?.title?.additionalInfo,
              ) && (
                <InfoDialog
                  information={
                    additionalInfo(
                      translateBackendString,
                      options?.title?.additionalInfo,
                    ) ?? ''
                  }
                />
              )}
            </Box>
          </Typography>
        )}
        {options?.subtitle && (
          <Typography
            variant='bodyLarge'
            component={'div'}
            sx={{
              mb: 3,
              table: { width: '100%' },
              display: 'flex',
              alignItems: 'end',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
                m: 0,
                '& > *': { m: 0 },
              }}
            >
              <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
                {translateBackendString(options?.subtitle)}
              </ReactMarkdown>
            </Box>
            {additionalInfo(
              translateBackendString,
              options?.subtitle?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.subtitle?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Typography>
        )}
      </Box>
      <SelectionTile
        testId='investment-knowledge'
        onChange={(e: any) =>
          updateUser({ ...userData, investmentKnowledge: e.target.value })
        }
        value={userData.investmentKnowledge ?? ''}
        options={options?.options?.map((option: any) => ({
          value: option.value,
          label: translateBackendString(option.label),
          subtitle: translateBackendString(option.description),
        }))}
        error={errors?.investmentKnowledge?.active}
      />
    </WorkflowCompletionForm>
  );
};

export default InvestmentKnowledgeVisual;
